import { businesses, profile } from '~api';
import { Business } from '~models';
import i18n from '../i18n';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';
import sortBy from 'lodash/sortBy';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

const ADD_EMAIL = 'ADD_EMAIL';
const DELETE_EMAIL = 'DELETE_EMAIL';
const ADD_LAUNCH_DATE = 'ADD_LAUNCH_DATE';
const SET_BUSINESS = 'SET_BUSINESS';
const SET_BUSINESSES = 'SET_BUSINESSES';
const SET_BUSINESS_PROGRAM = 'SET_BUSINESS_PROGRAM';
const SET_BUSINESS_GOALS = 'SET_BUSINESS_GOALS';
const RESET_BUSINESS = 'RESET_BUSINESS';
const TRIGGER_LOCKED_MEDITATION = 'TRIGGER_LOCKED_MEDITATION';
const LOADING = 'LOADING';

const state = {
    business: { ...new Business() },
    businesses: [],
    goals: {
        consecutiveDays: {
            isActive: false,
            params: {
                '3_daily_watch': false,
                '5_daily_watch': false,
                '10_daily_watch': false
            }
        }
    },
    isLoading: false
};

const actions = {
    /**
     * Reset business state and redirect to business list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_BUSINESS);
        router.push({ name: 'dashboard.businesses' });
    },

    /**
     * Reset business state
     * @param {function} commit
     */
    resetBusiness({ commit }) {
        commit(RESET_BUSINESS);
    },

    /**
     * Reset account password
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async resetPassword({ commit, dispatch, state }) {
        const successMessage = i18n.t(
            'business.edit.settings.reset.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.reset'),
            { root: true }
        );

        if (!confirmed) return;

        commit(LOADING);

        // Api request on password reset
        const resp = await businesses.resetPassword(state.business.id);

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
    },

    /**
     * Create business account
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('business.create.successMessage');

        commit(LOADING);

        const {
            name,
            maxUsers,
            domain,
            isChallenge,
            owner: { email, fullName, phone },
            definitions
        } = cloneDeep(state.business);

        definitions.sessions = definitions.sessions.map(
            ({ id, showAt, isLocked }) => {
                return {
                    id,
                    isLocked: isLocked || false,
                    showAt: dayjs(showAt)
                };
            }
        );

        definitions.emails = definitions.emails.map(({ sendAt, ...rest }) => {
            return {
                sendAt: dayjs(sendAt),
                ...rest
            };
        });

        definitions.program.startedAt = dayjs(definitions.program.startedAt);
        definitions.program.endedAt = dayjs(definitions.program.endedAt);

        // Api request to create business
        const resp = await businesses.create({
            name,
            domain,
            maxUsers: Number(maxUsers),
            isChallenge,
            owner: {
                email,
                fullName,
                phone
            },
            definitions
        });

        if (resp.ok) {
            commit(RESET_BUSINESS);
        }

        // Handle response
        Handler.handleResponse(
            resp,
            successMessage,
            '',
            'dashboard.businesses'
        );

        commit(LOADING);
    },

    /**
     * Add email to program email list
     * @param {function} commit
     */
    async addEmail({ commit }, params) {
        await commit(ADD_EMAIL, params);
    },

    /**
     * Delete email to program email list
     * @param {function} commit
     */
    async deleteEmail({ commit }, index) {
        await commit(DELETE_EMAIL, index);
    },

    /**
     * Get Business account
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, id) {
        commit(LOADING);

        // Api request get business account by id
        const resp = await businesses.read(id);

        if (resp.ok) {
            // Fix for old businesses
            if (resp.data.isChallenge === undefined) {
                resp.data.isChallenge = false;
            }

            commit(SET_BUSINESS, resp.data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);
    },

    /**
     * Update Business account
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        commit(LOADING);

        const successMessage = i18n.t('business.edit.successMessage');

        const {
            name,
            maxUsers,
            domain,
            isChallenge,
            owner: { email, fullName, phone },
            definitions
        } = cloneDeep(state.business);

        definitions.sessions = definitions.sessions.map(
            ({ id, showAt, isLocked }) => {
                return {
                    id,
                    isLocked: isLocked || false,
                    showAt: dayjs(showAt)
                };
            }
        );

        definitions.emails = definitions.emails.map(
            ({ subject, title, message, sendAt }) => {
                return {
                    sendAt: dayjs(sendAt),
                    subject,
                    title,
                    message
                };
            }
        );

        definitions.program.startedAt = dayjs(definitions.program.startedAt);
        definitions.program.endedAt = dayjs(definitions.program.endedAt);

        const resp = await businesses.update(state.business.id, {
            name,
            domain,
            maxUsers: Number(maxUsers),
            isChallenge,
            owner: {
                email,
                fullName,
                phone
            },
            definitions
        });

        Handler.handleResponse(
            resp,
            successMessage,
            '',
            'dashboard.businesses'
        );

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_BUSINESS);
        }

        commit(LOADING);
    },

    /**
     * Upload Avatar  account
     * @param data {object} data
     * @returns {Promise<any>}
     */
    async uploadAvatar({ commit }, data) {
        commit(LOADING);

        const successMessage = i18n.t('business.edit.successMessageAvatar');
        const resp = await profile.uploadCoverPhoto(data);

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Upload Avatar  account
     * @param data {object} data
     * @returns {Promise<any>}
     */
    async removeAvatar({ commit }, id) {
        commit(LOADING);

        const successMessage = i18n.t(
            'business.edit.successMessageAvatarDeleted'
        );
        const resp = await profile.removeCoverPhoto(id);

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Activate/Deactivate account
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    updateStatus: async function({ commit, state }) {
        const { id, isActive } = state.business;

        // Set success message based on value isActive
        let successMessage = isActive
            ? i18n.t(
                  'business.edit.settings.deactivate.successMessageActivated'
              )
            : i18n.t(
                  'business.edit.settings.deactivate.successMessageDeactivated'
              );

        commit(LOADING);

        // Api request on update status
        const resp = await businesses.updateStatus(id, { isActive });

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
    },

    /**
     * Delete Business account.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @returns {Promise<void>}
     */
    async deleteAccount({ commit, dispatch, state }, data = undefined) {
        let redirectName = 'dashboard.businesses';
        let businessId;

        if (data && data.id) {
            businessId = data.id;
        } else {
            businessId = state.business.id;
        }

        const successMessage = i18n.t(
            'business.edit.settings.delete.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.delete'),
            { root: true }
        );
        if (!confirmed) return;

        commit(LOADING);

        // Api request on delete account
        const resp = await businesses.delete(businessId);

        // Handle response
        Handler.handleResponse(resp, successMessage, '', redirectName);

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_BUSINESS);
        }

        commit(LOADING);

        return resp.ok;
    },

    async getterBusinessesWithDepartments({ commit }) {
        let businessesList = [];

        const params = {
            flat: true
        };
        const resp = await businesses.list(params);

        if (resp.ok) {
            resp.data.map(item => {
                let departmentsList = [];

                if (item.userDepartments && item.userDepartments.length) {
                    item.userDepartments.map(department => {
                        if (department) {
                            departmentsList.push({
                                title: department,
                                value: department
                            });
                        }
                    });
                }

                if (departmentsList.length > 1) {
                    departmentsList = sortBy(departmentsList, [
                        item => item.title.toLowerCase()
                    ]);
                }

                businessesList.push({
                    title: item.name,
                    value: item.id,
                    departments: departmentsList
                });
            });

            businessesList = sortBy(businessesList, [
                item => item.title.toLowerCase()
            ]);

            commit(SET_BUSINESSES, businessesList);
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },
    async getterBusinessesWithFields({ commit }) {
        let businessesList = [];

        const params = {
            flat: true
        };
        const resp = await businesses.list(params);

        if (resp.ok) {
            resp.data.map(item => {
                let customFields = [];

                if (item.customUserFields && item.customUserFields.length) {
                    customFields = item.customUserFields;
                }

                businessesList.push({
                    text: item.name,
                    value: item.id,
                    customFields,
                    maxUsers: item.maxUsers || 0
                });
            });

            businessesList = sortBy(businessesList, [
                item => item.text.toLowerCase()
            ]);

            commit(SET_BUSINESSES, businessesList);
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },

    addLaunchDate({ commit }, params) {
        commit(ADD_LAUNCH_DATE, params);
    },

    triggerLockedMeditation({ commit }, params) {
        commit(TRIGGER_LOCKED_MEDITATION, params);
    },

    /**
     * Set business program from template
     * @param {function} commit
     * @param {function} rootGetters
     */
    setBusinessProgram({ commit, rootGetters }) {
        commit(SET_BUSINESS_PROGRAM, rootGetters['programs/program']);
    },

    /**
     * Get Business account golas
     * @param {function} commit
     * @param  {string} id
     */
    async readGoals({ commit }, id) {
        commit(LOADING);

        // Api request get business account by id
        const resp = await businesses.readGoals(id);

        if (resp.ok) {
            // Fix for old businesses
            commit(SET_BUSINESS_GOALS, resp.data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [ADD_EMAIL](state, params) {
        if (params.index === -1) {
            state.business.definitions.emails = [
                ...state.business.definitions.emails,
                params.data
            ];
        } else {
            state.business.definitions.emails[params.index] = params.data;
        }
    },
    [DELETE_EMAIL](state, index) {
        if (index > -1) {
            state.business.definitions.emails.splice(index, 1);
        }
    },
    [ADD_LAUNCH_DATE](state, params) {
        if (params.routingKey === 'meditation') {
            const meditationIndex = state.business.definitions.sessions.findIndex(
                item => item.id === params.id
            );

            state.business.definitions.sessions[meditationIndex].showAt = dayjs(
                params.date
            );
        } else {
            state.business.definitions.emails[params.itemIndex].sendAt = dayjs(
                params.date
            );
        }
    },
    [TRIGGER_LOCKED_MEDITATION](state, params) {
        const meditationIndex = state.business.definitions.sessions.findIndex(
            item => item.id === params.id
        );

        state.business.definitions.sessions[meditationIndex].isLocked =
            params.value;
    },
    [SET_BUSINESS](state, data) {
        state.business = data;
    },
    [SET_BUSINESS_PROGRAM](state, data) {
        const { sessions, emails } = data;
        state.business.definitions.sessions = [...sessions];
        state.business.definitions.emails = [...emails];
    },
    [SET_BUSINESSES](state, data) {
        state.businesses = data;
    },
    [SET_BUSINESS_GOALS](state, data) {
        state.goals = data;
    },
    [RESET_BUSINESS](state) {
        state.business = { ...new Business() };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading,
    businesses: state => state.businesses,
    consecutiveDays: state => state.goals.consecutiveDays,
    isChallenge: state => state.business.isChallenge,
    emails: state => state.business.definitions.emails
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
